import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import 'katex/dist/katex.min.css';

/**
 * MarkdownMathRenderer Component
 *
 * Renders Markdown content with embedded LaTeX mathematical expressions.
 * Supports:
 * - Full Markdown syntax (headings, lists, bold, italic, links, etc.)
 * - Inline math: $x = 5$ or \(x = 5\)
 * - Block math: $$y = 2x + 1$$ or \[y = 2x + 1\]
 * - GitHub Flavored Markdown (tables, strikethrough, task lists)
 * - Code blocks with syntax highlighting
 *
 * @example
 * ```tsx
 * <MarkdownMathRenderer>
 *   **Step 1:** Solve for x when $x = 5$
 *
 *   The equation is:
 *   $$y = 2x + 1$$
 * </MarkdownMathRenderer>
 * ```
 */

interface MarkdownMathRendererProps {
  /** Markdown content with optional LaTeX math expressions */
  children: string;
  /** Additional CSS classes */
  className?: string;
  /** Display mode: 'inline' for inline content, 'block' for block content */
  display?: 'inline' | 'block';
}

export const MarkdownMathRenderer: React.FC<MarkdownMathRendererProps> = ({
  children,
  className = '',
  display = 'block'
}) => {
  // Handle empty or invalid content
  if (!children || typeof children !== 'string') {
    return null;
  }

  const content = children.trim();
  if (content.length === 0) {
    return null;
  }

  const containerClassName = display === 'block'
    ? `markdown-math-content ${className}`
    : `markdown-math-inline ${className}`;

  return (
    <div className={containerClassName}>
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[rehypeKatex]}
        components={{
          // Customize rendering of specific elements if needed
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="ml-2">{children}</li>,
          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          code: ({ className, children, ...props }) => {
            // Inline code
            const match = /language-(\w+)/.exec(className || '');
            return !match ? (
              <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props}>
                {children}
              </code>
            ) : (
              // Block code
              <code className={`${className} block bg-gray-100 p-2 rounded text-sm font-mono overflow-x-auto`} {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => <pre className="mb-2">{children}</pre>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2">
              {children}
            </blockquote>
          ),
          a: ({ children, href }) => (
            <a href={href} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          h1: ({ children }) => <h1 className="text-2xl font-bold mb-2 mt-4">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-bold mb-2 mt-3">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-bold mb-2 mt-2">{children}</h3>,
          h4: ({ children }) => <h4 className="text-base font-bold mb-1 mt-2">{children}</h4>,
          table: ({ children }) => (
            <table className="border-collapse border border-gray-300 mb-2">
              {children}
            </table>
          ),
          th: ({ children }) => (
            <th className="border border-gray-300 px-2 py-1 bg-gray-100 font-bold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-gray-300 px-2 py-1">
              {children}
            </td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownMathRenderer;
